import React, { FC, forwardRef, useContext } from 'react';
import clsx from 'clsx';
import styles from './AddToCartButton.module.css';
import { Button, ButtonProps } from '../Button';
import { BlackcartContext } from '../../context';

/**
 * @interface IAddToCartButtonProps
 * @property {ButtonProps} buttonProps - props that applicable to Button component;
 *
 * @property {function} onClick - this callback is fired when a user clicks on
 *  Try before you Buy button. Should be used to
 *  modify a storefront cart by adding the item to cart and setting this item with
 *  `isTBYB` flag set to true when updating a Blackcart cart via
 *  setCart method provided from useBlackcart hook.
 *
 *  If instantCheckout is provided this callback can be waived, but it's recommended to
 *  provide it. In case cart is invalid for TBYB experience this method will be called to
 *  handle it on a storefront.
 *
 * @property {boolean} fullWidth - Default to false. the component will take full width of its
 *  parent container. Button will share this property if provided.
 *
 * @property {string} featureName - Default to Try before you buy. Can be customized
 *  if set.
 *
 * @property {function} onLearnMoreClick - Optional. If useModal set in BlackcartProvider
 *  the Javascript Info Modal will be shown. Otherwise, this callback is called
 *  whenever a user clicks on Learn more about {featureName} link.
 *
 * @property {boolean} hideInfoCaption - Default to false. if set to true Learn more
 *  caption will be hidden.
 * */

interface IAddToCartButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLDivElement>, 'children' | 'onClick'> {
  buttonProps: ButtonProps;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  hideInfoCaption?: boolean;
  fullWidth?: boolean;
  featureName?: string;
  onLearnMoreClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const AddToCartButton: FC<IAddToCartButtonProps> = forwardRef<
  HTMLDivElement,
  IAddToCartButtonProps
>((props, ref) => {
  const {
    buttonProps,
    className,
    fullWidth = false,
    featureName = 'Try before you buy',
    onLearnMoreClick,
    hideInfoCaption,
    onClick,
    ...restProps
  } = props;

  const { useModal, showBlackcartModal } = useContext(BlackcartContext);

  const handleShowModal = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (useModal) {
      showBlackcartModal(event.currentTarget);
    }
    onLearnMoreClick && onLearnMoreClick(event);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClick(event);
  };

  return (
    <div
      id="bc-addToCart__root"
      className={clsx(
        styles.root,
        {
          [styles.full]: fullWidth
        },
        'bc-addToCart__root',
        className
      )}
      ref={ref}
      {...restProps}
    >
      <Button
        aria-label={`Add to ${featureName} cart`}
        {...buttonProps}
        full={fullWidth}
        onClick={handleClick}
      />
      {!hideInfoCaption && (
        <div id="bc-addToCart__info" className={clsx(styles.infoText, 'bc-addToCart__info')}>
          <p>
            Learn more about&nbsp;
            <a
              className={clsx(styles.learnMore, 'bc-addToCart__info--link', className)}
              onClick={handleShowModal}
              aria-label={`Learn more about ${featureName}`}
            >
              {featureName}
            </a>
          </p>
        </div>
      )}
    </div>
  );
});

export { AddToCartButton };
